import React from "react";
import { AnonymousPermitFormProps, PermitFormProps } from "../../../types/forms/Permits/PermitFormProps";
import useHullCleaningPermitForm from "../../../hooks/useHullCleaningPermitForm";
import LoadingSpinner from "../../Common/LoadingSpinner";
import PermitHeader from "./PermitHeader";
import { PermitType } from "../../../common/enums";
import DangerAlert from "../../Common/DangerAlert";
import PermitActionBar from "./PermitActionBar";
import DateTimeInput from "../../Common/DateTimeInput";
import RemindMe from "./RemindMe";
import TextInput from "../../Common/TextInput";
import TextArea from "../../Common/TextArea";
import RadioButton from "../../Common/RadioButton";
import AdminComment from "./AdminComment";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import Button from "../../Common/Button";
import SignatureModal from "../../Signatures/SignatureModal";
import RejectPermitModal from "../RejectPermitModal";
import AcceptUnsavedChangesModal from "./AcceptUnsavedChangesModal";
import UpdateWithNotificationModal from "./UpdateWithNotificationModal";
import PositionPicker from "./PositionPicker";

function HullCleaningPermitForm(props: AnonymousPermitFormProps) {
    const formManager = useHullCleaningPermitForm(props);

    const { formState, storedPermit, department, formStateChangeHandler, isLoading } = formManager;
    const { resources } = props;

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <div className="mt-5 mb-5">
                <div className="container">
                    <div className="mb-5">
                        <PermitHeader
                            formState={formState}
                            resources={resources}
                            permitDocument={storedPermit}
                            permitType={PermitType.HullCleaningPermit}
                            permitNameResource={resources.get("HullCleaningPermit")}
                        />
                    </div>
                    {!!storedPermit && formManager.showRejectionComment ? (
                        <DangerAlert>{storedPermit.rejectionComment}</DangerAlert>
                    ) : null}
                    {storedPermit ? (
                        <PermitActionBar
                            acceptPermitClickHandler={formManager.acceptButtonClickHandler}
                            copyPermitClickHandler={formManager.copyButtonClickHandler}
                            rejectPermitClickHandler={formManager.rejectPermitClickHandler}
                            renewPermitClickHandler={formManager.renewPermitClickHandlerAsync}
                            department={department}
                            permitDocument={storedPermit}
                            resources={props.resources}
                            requestInProgress={formManager.requestInProgress}
                        />
                    ) : null}

                    <div className="mt-3 mb-3">
                        <h1 className="h6">{resources.get("Conditions0")}</h1>
                        <p>{resources.get("Conditions1")}</p>

                        <h6>{resources.get("Conditions2")}</h6>
                        <p>{resources.get("Conditions3")}</p>

                        <h6>{resources.get("Conditions4")}</h6>
                        <ul>
                            <li>
                                {resources.get("Conditions5")} (
                                <a href="mailto:miljoforvaltningen@miljo.goteborg.se">
                                    miljoforvaltningen@miljo.goteborg.se
                                </a>
                                )
                            </li>
                            <li>{resources.get("Conditions6")}</li>
                        </ul>
                    </div>

                    <form onSubmit={formManager.submitHandler}>
                        <PositionPicker
                            canManagePositions={formManager.canManagePositions}
                            canManagePositionIsActive={formManager.canManagePositions}
                            canManagePolygons={formManager.canManagePositionPolygons}
                            canManageComments={formManager.canManagePositions}
                            onPositionAdded={formManager.addPositionPickerRowHandler}
                            onPositionChange={formManager.updatePositionPickerRowHandler}
                            onPositionRemoved={formManager.removePositionPickerRowHandler}
                            onUpdatePositionsClicked={formManager.updatePositionsClickHandlerAsync}
                            positions={formState.positions}
                            resources={props.resources}
                            showUpdatePositionsButton={formManager.showUpdatePositionsButton}
                            canManageCategories={false}
                            selectedCategories={[]}
                            showActiveStatus={true}
                            showCategories={false}
                        />

                        <div className="row mt-3">
                            <DateTimeInput
                                defaultValue={formState.startDate.value ?? new Date()}
                                id="start-date"
                                label={resources.get("StartDateTime")}
                                onBlur={formManager.startDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.startDate.errorMessage}
                                ref={formManager.startDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                            <DateTimeInput
                                defaultValue={formState.endDate.value ?? new Date()}
                                id="end-date"
                                label={resources.get("EndDateTime")}
                                onBlur={formManager.endDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.endDate.errorMessage}
                                minValue={formState.startDate.value}
                                ref={formManager.endDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="mt-3">
                            <RemindMe
                                formState={formState}
                                onChange={formManager.registerFormStateChange}
                                resources={resources}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.shipName.value}
                                errorMessage={formState.shipName.errorMessage}
                                inputId="ship-name"
                                labelText={props.resources.get("ShipName")}
                                onChange={(s) => formStateChangeHandler("shipName", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.callSign.value}
                                errorMessage={formState.callSign.errorMessage}
                                inputId="call-sign"
                                labelText={props.resources.get("CallSign")}
                                onChange={(s) => formStateChangeHandler("callSign", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.entrepreneur.value}
                                errorMessage={formState.entrepreneur.errorMessage}
                                inputId="ship-name"
                                labelText={props.resources.get("Entrepreneur")}
                                onChange={(s) => formStateChangeHandler("entrepreneur", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextArea
                                value={formState.description.value}
                                errorMessage={formState.description.errorMessage}
                                labelText={props.resources.get("WorkPurposeDescription")}
                                onChange={(s) => formStateChangeHandler("description", s)}
                                textAreaId="description"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                inputId="contact-person__name"
                                containerClassName="col-md-6"
                                value={formState.contactPerson.name}
                                errorMessage={formState.contactPerson.nameErrorMessage}
                                labelText={resources.get("ContactPerson")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("contactPerson", {
                                        ...formState.contactPerson,
                                        name: s
                                    })
                                }
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                inputId="contact-person__phone-number"
                                containerClassName="col-md-6"
                                value={formState.contactPerson.phoneNumber}
                                errorMessage={formState.contactPerson.phoneNumberErrorMessage}
                                labelText={resources.get("ContactPersonPhoneNumber")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("contactPerson", {
                                        ...formState.contactPerson,
                                        phoneNumber: s
                                    })
                                }
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                inputId="ship-route"
                                containerClassName="col-md-6"
                                value={formState.shipRoute.value}
                                errorMessage={formState.shipRoute.errorMessage}
                                labelText={resources.get("WhichRouteIsUsed")}
                                onChange={(s) => formStateChangeHandler("shipRoute", s)}
                                readOnly={!formManager.canManageContent}
                            />

                            <TextInput
                                inputId="color-system"
                                containerClassName="col-md-6"
                                value={formState.colorSystem.value}
                                errorMessage={formState.colorSystem.errorMessage}
                                labelText={resources.get("WhichColorSystemIsUsed")}
                                onChange={(s) => formStateChangeHandler("colorSystem", s)}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                inputId="cleaning-method"
                                containerClassName="col-md-6"
                                value={formState.cleaningMethod.value}
                                errorMessage={formState.cleaningMethod.errorMessage}
                                labelText={resources.get("CleaningMethod")}
                                onChange={(s) => formStateChangeHandler("cleaningMethod", s)}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="mt-3">
                            <span className="fw-bold">
                                {resources.get("IsMethodApprovedByEnvironmentalManagement")}
                            </span>
                            <RadioButton
                                checked={formState.methodIsApproved.value}
                                inputId="method-is-approved__yes"
                                label={resources.get("Yes")}
                                onSelect={() => formStateChangeHandler("methodIsApproved", true)}
                                name="method-is-approved"
                                readOnly={!formManager.canManageContent}
                            />
                            <RadioButton
                                checked={!formState.methodIsApproved.value}
                                inputId="method-is-approved__no"
                                label={resources.get("No")}
                                onSelect={() => formStateChangeHandler("methodIsApproved", false)}
                                name="method-is-approved"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>
                        <AdminComment
                            formElement={formState.adminComment}
                            onChange={(s) => formStateChangeHandler("adminComment", s)}
                            readonly={!formManager.canManageAdminComment}
                            onUpdateButtonClick={formManager.updateAdminCommentButtonClickHandlerAsync}
                            showUpdateButton={formManager.showUpdateAdminCommentButton}
                            resources={resources}
                            show={formManager.showAdminComment}
                            containerClassName="mt-4"
                        />

                        {formManager.showAnonymousUserEmail ? (
                            <TextInput
                                containerClassName="mt-3 col-md-6"
                                inputId="anonymous-user-email"
                                labelText={resources.get("AnonymousUserEmail")}
                                onChange={(s) => formStateChangeHandler("anonymousUserEmail", s)}
                                value={formState.anonymousUserEmail.value}
                                errorMessage={formState.anonymousUserEmail.errorMessage}
                                readOnly={!formManager.canManageContent}
                            />
                        ) : null}

                        <SignatureDisplay
                            resources={resources}
                            signatureManager={formManager.permitProviderSignatureManager}
                            signatureLabel={resources.get("PermitProvider")}
                            containerClassName="mt-3 mb-4"
                        />

                        <div className="d-flex mt-3">
                            {formManager.canCreate ? (
                                <Button type="submit" disabled={formManager.requestInProgress}>
                                    {resources.get("Send")}
                                </Button>
                            ) : formManager.canUpdate ? (
                                <Button type="submit" disabled={formManager.requestInProgress}>
                                    {resources.get("Update")}
                                </Button>
                            ) : null}

                            {formManager.canUpdateWithNotification ? (
                                <Button
                                    className="ms-2"
                                    type="button"
                                    disabled={formManager.requestInProgress}
                                    onClick={() => formManager.setShowUpdateWithNotificationModal(true)}
                                >
                                    {resources.get("UpdateWithNotification")}
                                </Button>
                            ) : null}
                        </div>
                    </form>
                </div>
            </div>
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.permitProviderSignatureManager}
                ref={formManager.permitProviderSignatureManager.signaturePadRef}
            />
            <RejectPermitModal
                onHide={() => formManager.setShowRejectPermitModal(false)}
                onReject={formManager.rejectPermitHandlerAsync}
                resources={resources}
                show={formManager.showRejectPermitModal}
            />
            <AcceptUnsavedChangesModal
                show={formManager.showAcceptUnsavedChangesModal}
                onHide={() => formManager.setShowAcceptUnsavedChangesModal(false)}
                onAccept={formManager.unsavedChangesModalAcceptHandlerAsync}
                onUpdate={formManager.unsavedChangesModalUpdateHandlerAsync}
                resources={resources}
            />
            <UpdateWithNotificationModal
                permitDocument={storedPermit}
                show={formManager.showUpdateWithNotificationModal}
                onHide={() => formManager.setShowUpdateWithNotificationModal(false)}
                resources={resources}
                onUpdate={formManager.updateWithNotificationHandlerAsync}
            />
        </>
    );
}

export default HullCleaningPermitForm;
